/**
 * Pagina /superadmin/catalogo — Sprint 13 S13-27.
 *
 * Centraliza todos los catalogos FIJOS del sistema (enums con CHECK
 * constraint en BD) en una sola pagina. Reemplaza las paginas
 * separadas de Sectores y Tipos de recurso. Read-only por diseño:
 * agregar o renombrar valores requiere migracion Alembic + cambio
 * doctrinario, no operacion via UI.
 *
 * Secciones:
 * - Sectores del barrio (2)
 * - Tipos de recurso (5)
 * - Estados de tenencia (5)
 * - Estados de cuota (5)
 * - Estados de proyecto (5)
 * - Tipos de categoría (2)
 *
 * RBAC ruta: super_admin (sa).
 */
import { Card } from '../../components/ui/Card';
import { PageHeader } from '../../components/layout/PageHeader';

interface Item {
  codigo: string;
  nombre: string;
  descripcion?: string;
  prefijo?: string;
  badge?: { texto: string; color: 'success' | 'warning' | 'danger' | 'muted' };
}

interface Catalogo {
  titulo: string;
  descripcion: string;
  items: Item[];
}

const COLOR_BADGE: Record<NonNullable<Item['badge']>['color'], string> = {
  success: 'bg-state-success/15 text-state-success',
  warning: 'bg-state-warning/15 text-state-warning',
  danger: 'bg-state-danger/15 text-state-danger',
  muted: 'bg-bg-elevated text-text-muted',
};

const CATALOGOS: Catalogo[] = [
  {
    titulo: 'Sectores del barrio',
    descripcion:
      'Sector al que pertenece cada casa. Calbucura usa Oriente / Poniente.',
    items: [
      {
        codigo: 'Oriente',
        nombre: 'Oriente',
        descripcion: 'Sector oriente del barrio.',
      },
      {
        codigo: 'Poniente',
        nombre: 'Poniente',
        descripcion: 'Sector poniente del barrio.',
      },
    ],
  },
  {
    titulo: 'Tipos de recurso',
    descripcion:
      'Tipos físicos asignables a cada casa. Identificador correlativo automático según prefijo.',
    items: [
      {
        codigo: 'control_remoto',
        nombre: 'Control remoto',
        prefijo: 'CR',
        descripcion: 'Control del portón vehicular.',
        badge: { texto: 'No suspendible', color: 'muted' },
      },
      {
        codigo: 'llavero',
        nombre: 'Llavero',
        prefijo: 'LL',
        descripcion: 'Acceso peatonal físico.',
        badge: { texto: 'No suspendible', color: 'muted' },
      },
      {
        codigo: 'citofonia',
        nombre: 'Citofonía',
        prefijo: 'CT',
        descripcion: 'Acceso al sistema de citofonía. Se crea directo en estado entregado.',
        badge: { texto: 'Suspendible', color: 'warning' },
      },
      {
        codigo: 'acceso_app_wifi',
        nombre: 'Acceso Wi-Fi',
        prefijo: 'WF',
        descripcion: 'Habilitación del Wi-Fi del barrio.',
        badge: { texto: 'Suspendible', color: 'warning' },
      },
      {
        codigo: 'otro',
        nombre: 'Otro',
        prefijo: 'OT',
        descripcion: 'Categoría residual para recursos no clasificables.',
        badge: { texto: 'No suspendible', color: 'muted' },
      },
    ],
  },
  {
    titulo: 'Estados de tenencia',
    descripcion:
      'Tipo de vínculo entre una persona y una casa (campo Tenencia.tipo).',
    items: [
      {
        codigo: 'propietario_residente',
        nombre: 'Propietario residente',
        descripcion: 'Dueño que habita la casa.',
        badge: { texto: 'Vive aquí', color: 'success' },
      },
      {
        codigo: 'propietario_no_residente',
        nombre: 'Propietario no residente',
        descripcion: 'Dueño que NO habita la casa (arrendada o desocupada).',
      },
      {
        codigo: 'arrendatario',
        nombre: 'Arrendatario',
        descripcion: 'Habita la casa bajo contrato de arriendo.',
        badge: { texto: 'Vive aquí', color: 'success' },
      },
      {
        codigo: 'comodatario',
        nombre: 'Comodatario',
        descripcion: 'Habita la casa por cesión gratuita.',
        badge: { texto: 'Vive aquí', color: 'success' },
      },
      {
        codigo: 'otro',
        nombre: 'Otro',
        descripcion: 'Categoría residual.',
      },
    ],
  },
  {
    titulo: 'Estados de cuota',
    descripcion:
      'Ciclo de vida de una cuota semestral (campo Cuota.estado).',
    items: [
      {
        codigo: 'pendiente',
        nombre: 'Pendiente',
        descripcion: 'Generada, sin pagos aplicados.',
        badge: { texto: 'Por pagar', color: 'warning' },
      },
      {
        codigo: 'en_abono',
        nombre: 'En abono',
        descripcion: 'Pagada parcialmente. Saldo > 0.',
        badge: { texto: 'Por pagar', color: 'warning' },
      },
      {
        codigo: 'pagada',
        nombre: 'Pagada',
        descripcion: 'Suma de distribuciones = monto_total.',
        badge: { texto: 'OK', color: 'success' },
      },
      {
        codigo: 'vencida',
        nombre: 'Vencida',
        descripcion: 'Pasó la fecha de vencimiento sin pagarse del todo.',
        badge: { texto: 'Atraso', color: 'danger' },
      },
      {
        codigo: 'condonada',
        nombre: 'Condonada',
        descripcion: 'La directiva decidió no cobrarla. Lleva motivo de condonación.',
        badge: { texto: 'Anulada', color: 'muted' },
      },
    ],
  },
  {
    titulo: 'Estados de proyecto',
    descripcion:
      'Ciclo de vida de las iniciativas con presupuesto (campo Proyecto.estado).',
    items: [
      {
        codigo: 'propuesto',
        nombre: 'Propuesto',
        descripcion: 'En evaluación; aún no aprobado por la directiva.',
      },
      {
        codigo: 'aprobado',
        nombre: 'Aprobado',
        descripcion: 'Aprobado por acta; pendiente de comenzar la ejecución.',
        badge: { texto: 'Activo', color: 'warning' },
      },
      {
        codigo: 'en_ejecucion',
        nombre: 'En ejecución',
        descripcion: 'Trabajos en curso. Acumula gastos.',
        badge: { texto: 'Activo', color: 'success' },
      },
      {
        codigo: 'cerrado',
        nombre: 'Cerrado',
        descripcion: 'Terminado con fin_real registrado.',
        badge: { texto: 'Terminado', color: 'muted' },
      },
      {
        codigo: 'cancelado',
        nombre: 'Cancelado',
        descripcion: 'Suspendido sin completar (soft delete).',
        badge: { texto: 'Cancelado', color: 'danger' },
      },
    ],
  },
  {
    titulo: 'Tipos de categoría',
    descripcion:
      'Clasificación de las categorías financieras (campo Categoria.tipo).',
    items: [
      {
        codigo: 'ingreso',
        nombre: 'Ingreso',
        descripcion: 'Categorías que clasifican entradas de dinero (cuotas, multas, otros ingresos).',
        badge: { texto: 'Entra', color: 'success' },
      },
      {
        codigo: 'gasto',
        nombre: 'Gasto',
        descripcion: 'Categorías que clasifican salidas de dinero (servicios, mantención, proyectos).',
        badge: { texto: 'Sale', color: 'warning' },
      },
    ],
  },
];

export function CatalogoSistemaPage() {
  return (
    <div className="px-8 py-6 space-y-8">
      <PageHeader
        eyebrow="Mantenedor — Catálogo doctrinario"
        title="Catálogo del sistema"
        description="Valores fijos definidos en el modelo de datos (CHECK constraint en base de datos). Esta página los lista como referencia. Para agregar, renombrar o eliminar un valor se requiere migración Alembic y cambio doctrinario, no operación vía UI."
      />

      {CATALOGOS.map((cat) => (
        <section key={cat.titulo} className="space-y-3">
          <div>
            <h2 className="text-lg font-medium text-text-primary">
              {cat.titulo}
            </h2>
            <p className="text-sm text-text-secondary mt-0.5">
              {cat.descripcion}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cat.items.map((item) => (
              <Card key={item.codigo}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-medium text-text-primary">
                      {item.nombre}
                    </div>
                    <div className="text-xs text-text-muted font-mono mt-0.5">
                      <code>{item.codigo}</code>
                      {item.prefijo && <> · prefijo {item.prefijo}-NNN</>}
                    </div>
                  </div>
                  {item.badge && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${COLOR_BADGE[item.badge.color]}`}
                    >
                      {item.badge.texto}
                    </span>
                  )}
                </div>
                {item.descripcion && (
                  <p className="text-sm text-text-secondary mt-2">
                    {item.descripcion}
                  </p>
                )}
              </Card>
            ))}
          </div>
        </section>
      ))}

      <div className="bg-bg-elevated border-l-4 border-admin-red rounded-sm p-4 max-w-3xl text-sm text-text-secondary">
        <strong className="text-text-primary">Doctrina:</strong> los enums
        de dominio se modelan como <code>string + CHECK constraint</code>{' '}
        (convención #4 del proyecto), no como tipos Postgres ENUM. Esto
        permite agregar valores con migración mínima, pero NO desde UI:
        toda modificación pasa por <code>app/models/</code> y una
        revisión Alembic.
      </div>
    </div>
  );
}
